import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { track, initAnalytics, endSession } from '../services/analytics';
import { speciesData, allSpecies, categoryConfig } from '../data/speciesData';
import AppShell from './layout/AppShell';
import FieldJournalPanel from './explorer/FieldJournalPanel';
import DetailPanel from './explorer/DetailPanel';
import MapView from './explorer/MapView';

// Mapbox access token
import mapboxgl from 'mapbox-gl';
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

if (!mapboxgl.accessToken) {
  console.error('MAPBOX ERROR: Token not found. Create a .env file with VITE_MAPBOX_TOKEN=your_token');
}

const WildlifeMigrationTracker = () => {
  const [selectedSpecies, setSelectedSpecies] = useState(null);
  const [activeCategories, setActiveCategories] = useState(Object.keys(categoryConfig));
  const [searchQuery, setSearchQuery] = useState('');
  const [searchParams] = useSearchParams();

  // Initialize analytics
  useEffect(() => {
    initAnalytics();
    track('page_view', { page: 'explorer' });
    const handleBeforeUnload = () => endSession();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Pre-select species from URL query param (e.g., /explorer?species=arctic-tern)
  useEffect(() => {
    const speciesParam = searchParams.get('species');
    if (speciesParam && allSpecies.find(s => s.id === speciesParam)) {
      setSelectedSpecies(speciesParam);
    }
  }, [searchParams]);

  const handleSpeciesSelect = (speciesId) => {
    setSelectedSpecies(speciesId);
    if (speciesId) {
      const species = allSpecies.find(s => s.id === speciesId);
      track('species_select', { species: species?.name || speciesId, category: species?.category });
    }
  };

  const handleCategoryClick = (category, event) => {
    const allKeys = Object.keys(categoryConfig);

    if (category === 'all') {
      setActiveCategories(allKeys);
      track('category_filter', { category: 'all', activeCategories: allKeys });
      return;
    }

    let newCategories;
    if (event?.shiftKey) {
      newCategories = activeCategories.includes(category)
        ? activeCategories.filter(c => c !== category)
        : [...activeCategories, category];
    } else {
      if (activeCategories.length === 1 && activeCategories[0] === category) {
        newCategories = allKeys;
      } else {
        newCategories = [category];
      }
    }
    setActiveCategories(newCategories);
    track('category_filter', { category, activeCategories: newCategories });
  };

  const filteredSpecies = allSpecies.filter(species => {
    const matchesCategory = activeCategories.includes(species.category);
    const matchesSearch = searchQuery === '' ||
      species.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      species.scientificName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const selectedSpeciesData = selectedSpecies
    ? allSpecies.find(s => s.id === selectedSpecies)
    : null;

  return (
    <AppShell
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
    >
      <MapView selectedSpecies={selectedSpecies} />

      <FieldJournalPanel
        species={filteredSpecies}
        activeCategories={activeCategories}
        onCategoryClick={handleCategoryClick}
        selectedSpecies={selectedSpecies}
        onSpeciesSelect={(id) => handleSpeciesSelect(id)}
      />

      <DetailPanel species={selectedSpeciesData} onClose={() => setSelectedSpecies(null)} />
    </AppShell>
  );
};

export default WildlifeMigrationTracker;
